import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';

import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails, postAllData } from '../../service/ProductService';
import { LIST_PARTIES, ROLLS_DROPDOWN, MANUAL_DISPATCH } from '../../config/ApiConstant';
import { SkeletonbodyTemplate, Skeletonitems } from '../../appconfig/Settings';

export const CreateDispatch = () => {
    document.title = 'Create Dispatch | Karan Enterprise';

    const navigate = useNavigate();
    const toast = useRef<any>(null);

    const [partyOptions, setPartyOptions] = useState<any[]>([]);
    const [selectedParty, setSelectedParty] = useState<any>(null);
    const [rollList, setRollList] = useState<any[]>([]);
    const [selectedRolls, setSelectedRolls] = useState<any[]>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        loadData();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const loadData = async () => {
        try {
            setPageLoad(false);
            const [partiesRes, rollsRes] = await Promise.all([
                getAllDetails(LIST_PARTIES),
                getAllDetails(ROLLS_DROPDOWN)
            ]);
            if (partiesRes) {
                const options = (partiesRes?.data || []).map((p: any) => ({
                    label: p.party_name,
                    value: p.id
                }));
                setPartyOptions(options);
            }
            if (rollsRes) {
                setRollList(rollsRes?.data || []);
            }
            setPageLoad(true);
        } catch (error) {
            setPageLoad(true);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Failed to load data. Please try again.'
            });
        }
    };

    const validate = () => {
        const newErrors: any = {};
        let isError = false;

        if (!selectedParty) {
            newErrors.party = 'Please select a party.';
            isError = true;
        }
        if (selectedRolls.length === 0) {
            newErrors.rolls = 'Please select at least one roll.';
            isError = true;
        }

        setErrors(newErrors);
        return !isError;
    };

    const onSubmit = async () => {
        if (!validate()) return;

        try {
            setSubmitLoading(true);
            const response = await postAllData(MANUAL_DISPATCH, {
                party_id: selectedParty,
                roll_ids: selectedRolls.map((r: any) => r.id)
            });
            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || 'Dispatch created successfully'
                });
                setTimeout(() => navigate('/dispatch-list'), 1000);
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: error.response?.data?.message || 'Something went wrong. Please try again.'
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const owrBodyTemplate = (rowData: any) => {
        const isOwr = rowData.is_owr === 1;
        return <Badge severity={isOwr ? 'success' : 'danger'} value={isOwr ? 'Yes' : 'No'} />;
    };

    const withoutGlaseBodyTemplate = (rowData: any) => {
        const val = rowData.without_glase === 1;
        return <Badge severity={val ? 'success' : 'danger'} value={val ? 'Yes' : 'No'} />;
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Dispatch'} pageName={'Create Dispatch'} pageTitle={'Create Dispatch'} />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />

                        {/* Party Selection */}
                        <div className="p-fluid mb-4">
                            <div className="field" style={{ maxWidth: '400px' }}>
                                <label htmlFor="party" className="font-semibold mb-2 block">
                                    Select Party <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="party"
                                    value={selectedParty}
                                    options={partyOptions}
                                    onChange={(e) => {
                                        setSelectedParty(e.value);
                                        setErrors((prev: any) => ({ ...prev, party: undefined }));
                                    }}
                                    placeholder="-- Select Party --"
                                    filter
                                    showClear
                                    className={errors.party ? 'p-invalid' : ''}
                                />
                                {errors.party && <small className="p-invalid-txt">{errors.party}</small>}
                            </div>
                        </div>

                        {/* Rolls Selection */}
                        <div className="mb-3">
                            <h5 className="mb-2">
                                Select Rolls <span className="text-red-500">*</span>
                                {selectedRolls.length > 0 && (
                                    <Badge value={`${selectedRolls.length} selected`} severity="info" className="ml-2" />
                                )}
                            </h5>
                            {errors.rolls && <small className="p-invalid-txt block mb-2">{errors.rolls}</small>}
                        </div>

                        {pageLoad ? (
                            <DataTable
                                className="datatable-responsive"
                                value={rollList}
                                selection={selectedRolls}
                                onSelectionChange={(e) => {
                                    setSelectedRolls(e.value);
                                    setErrors((prev: any) => ({ ...prev, rolls: undefined }));
                                }}
                                selectionMode="multiple"
                                dataKey="id"
                                paginator={rollList.length >= 10}
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                emptyMessage="No rolls available"
                                showGridlines
                                stripedRows
                            >
                                <Column selectionMode="multiple" style={{ width: '3rem' }} />
                                <Column field="roll_id" header="Roll ID" sortable />
                                <Column field="product_name" header="Product Name" sortable />
                                <Column field="width" header="Width" sortable />
                                <Column field="length" header="Length" sortable />
                                <Column field="is_owr" header="OWR" body={owrBodyTemplate} />
                                <Column field="without_glase" header="Without Glaze" body={withoutGlaseBodyTemplate} />
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                <Column style={{ width: '3rem' }} body={SkeletonbodyTemplate} />
                                <Column header="Roll ID" body={SkeletonbodyTemplate} />
                                <Column header="Product Name" body={SkeletonbodyTemplate} />
                                <Column header="Width" body={SkeletonbodyTemplate} />
                                <Column header="Length" body={SkeletonbodyTemplate} />
                                <Column header="OWR" body={SkeletonbodyTemplate} />
                                <Column header="Without Glaze" body={SkeletonbodyTemplate} />
                            </DataTable>
                        )}

                        {/* Actions */}
                        <div className="flex justify-content-end gap-2 mt-4">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-secondary mr-2"
                                onClick={() => navigate('/dispatch-list')}
                                disabled={submitLoading}
                            />
                            <Button
                                label="Submit Dispatch"
                                icon="pi pi-send"
                                loading={submitLoading}
                                onClick={onSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
